import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { SearchContactsQuery } from '@paintscout/api'
import { useOfflineQuotesQuery } from '@paintscout/api'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import { Offline, Online } from 'react-detect-offline'
import ContactSearchBar from 'src/components/common/ContactSearchBar'
import ContactTable from 'src/components/common/ContactTable'
import * as Sentry from '@sentry/react'
import { useHistory, useLocation } from 'react-router-dom'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import uniqBy from 'lodash/uniqBy'
import { useContactSearch } from 'shared/hooks'
import { getFiltersFromLocation, getQueryFromLocation } from 'src/components/common/QuoteSearchBar'
import type { ValueFilter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import type { UrlParams } from '../../locations/Locations/Locations'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {},
  searchBarFlexContainer: {
    display: 'flex'
  },
  newButton: {
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1)
    }
  },
  searchBarContainer: {
    flex: '1 1 auto',
    marginRight: 0,
    [theme.breakpoints.down('sm')]: {
      width: '190px'
    }
  },
  newButtonContainer: {
    flex: '0 1 auto',
    height: 'fit-content'
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }
}))

function ContactList() {
  return (
    <>
      <Online>
        <OnlineContacts />
      </Online>
      <Offline>
        <OfflineContacts />
      </Offline>
    </>
  )
}

const UPDATED_DATE_SORT = '-updated_date'

function OnlineContacts() {
  const location = useLocation<{ page?: number; bookmark?: string; sort?: number; fullLoad?: boolean }>()
  const urlParams = getUrlQuery<UrlParams>({ location })
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const history = useHistory()

  const [page, setPage] = useState(location.state?.page ?? 1)
  const [bookmark, setBookmark] = useState(location.state?.bookmark ?? undefined)
  const [filters, setFilters] = useState(getFiltersFromLocation(location))
  const [query, setQuery] = useState(getQueryFromLocation(location))
  // If searching sort by -<score>, if not sort by -updated_date
  const [sort, setSort] = useState(urlParams.sort ?? (query.length && query !== '*:*') ? '-<score>' : UPDATED_DATE_SORT) // Default sort, will be overridden if we query

  useEffect(() => {
    if (page || bookmark) {
      history.replace({
        ...history.location,
        state: {
          bookmark,
          page,
          sort
        }
      })
    }
  }, [page, bookmark])

  const limit = 20
  const [search, { loading, data, error, fetchMore, updateQuery }] = useContactSearch({
    // if we're on page 1 we'll also refetch to get latest results
    fetchPolicy: page === 1 ? (location?.state?.fullLoad ? 'no-cache' : 'cache-and-network') : 'cache-first',
    limit,
    prevBookmark: bookmark,
    prevSort: [sort],
    onCompleted: (newData) => {
      console.log({ newData, sort, limit, query, page })
      if (newData.searchContacts) {
        setBookmark(newData.searchContacts.bookmark)
      }
    }
  })
  const { total_rows = 0, contacts = [] } = data && data.searchContacts ? data.searchContacts : {}

  // show error toast
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar(`Unable to search Contacts`, { variant: 'error' })
    }
  }, [error])

  const handleRequestSort = (_ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`
    const cleanQuery =
      jsonToLucene({
        query: query ?? '',
        filters
      }) || '*:*'
    setPage(1)
    setUrlQuery({ location, params: { sort: newSort } })
    setSort(newSort)

    search({
      query: cleanQuery,
      sort: [newSort, UPDATED_DATE_SORT]
    })
  }

  const removeRowsFromQuery = (ids: string[]) => {
    updateQuery((cache: SearchContactsQuery) => {
      if (!cache.searchContacts) {
        return cache
      }

      return {
        ...cache,
        searchContacts: {
          ...cache.searchContacts,
          contacts: cache.searchContacts.contacts.filter((contact) => {
            return !ids.includes(contact._id)
          })
        }
      }
    })
  }

  return (
    <>
      <div className={classes.searchBarFlexContainer}>
        <div className={classes.searchBarContainer}>
          <ContactSearchBar
            useUrl
            loading={loading}
            filters={filters}
            onSearch={(text, filters, isInitialSearch) => {
              const adjustedFilters = [
                ...filters,
                {
                  id: 'trashed',
                  type: 'equal',
                  not: true,
                  value: true
                }
              ] as ValueFilter[]
              const query =
                jsonToLucene({
                  query: text ?? '',
                  filters: adjustedFilters
                }) || '*:*'

              setQuery(text)
              setFilters(filters)
              setSort(sort)

              if (!isInitialSearch) {
                setPage(1)
              }

              search({
                query,
                sort: [sort, UPDATED_DATE_SORT],
                limit
              })
            }}
          />
        </div>
      </div>
      <ContactTable
        skipRefetchQueries
        checkboxes={false}
        actions={{
          edit: true,
          delete: true
        }}
        loading={loading}
        contacts={contacts}
        page={page}
        sort={sort}
        pages={total_rows < limit ? 1 : Math.min(Math.ceil(total_rows / limit), 20)}
        totalRows={total_rows}
        rowsPerPage={limit}
        onPageChange={setPage}
        onFetchNextPage={() => {
          fetchMore()
        }}
        onDelete={(ids) => {
          removeRowsFromQuery(ids)
        }}
        onRequestSort={handleRequestSort}
      />
    </>
  )
}

function OfflineContacts() {
  const { data, error } = useOfflineQuotesQuery()

  const contacts = useMemo(
    () =>
      uniqBy(
        data?.offlineQuotes?.contacts.sort((a, b) => (a.updated_date > b.updated_date ? -1 : 1)) ?? [],
        (a) => a._id
      ),
    [data]
  )

  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
    }
  }, [error])

  return (
    <>
      <Typography variant="subtitle1" align="center" gutterBottom>
        You are offline - only recent Contacts are available
      </Typography>
      <ContactTable
        checkboxes={false}
        actions={{
          edit: true,
          delete: true
        }}
        contacts={contacts}
        page={1}
        pages={1}
        totalRows={contacts.length}
        rowsPerPage={contacts.length}
      />
    </>
  )
}

export default ContactList
